import type { QueryStep } from "@/api/queries";
import type { TraceSources } from "@/components/chat/TraceTimeline";
import type { TraceStep } from "./useRunStream";

/**
 * One row of an answer's timeline (PRD FR-5.1).
 *
 * A live event and a stored step describe the same execution from two sides —
 * the stream while it happens, the database afterwards — so both normalise into
 * this shape and the timeline component only ever renders one thing.
 */
export interface TraceRow {
  seq: number;
  tool: string;
  sql: string;
  rowCount: number | null;
  durationMs: number | null;
  truncated: boolean;
  outcome: "pending" | "ok" | "blocked" | "error";
  error: string | null;
  queryIndex: number | null;
  retryOf: number | null;
  /** Schema-qualified relations the statement referenced (PRD FR-5.2). */
  relations: string[];
  arguments: Record<string, unknown>;
  /** Where the step ran (MS-3): a source id when stored, a name when live.
   *  Both null for a staged join, which ran against no source. */
  connectionId: string | null;
  source: string | null;
  /** A staged join's `qN` inputs, and those truncated at their source. */
  inputs: number[];
  inputsTruncated: number[];
}

export const STAGED_TOOL = "query_staged_results";

/** A live event from the stream. */
export function fromLive(step: TraceStep): TraceRow {
  return { ...step, retryOf: null, connectionId: null };
}

/** A stored step from a persisted message. */
export function fromStored(step: QueryStep): TraceRow {
  return {
    seq: step.seq,
    tool: step.tool,
    sql: step.sql,
    rowCount: step.row_count,
    durationMs: step.duration_ms,
    truncated: step.truncated,
    outcome: step.outcome === "ok" ? "ok" : step.outcome === "blocked" ? "blocked" : "error",
    error: step.error_message,
    queryIndex: step.query_index,
    retryOf: step.retry_of,
    relations: step.relations ?? [],
    arguments: (step.arguments ?? {}) as Record<string, unknown>,
    connectionId: step.connection_id ?? null,
    source: typeof step.arguments?.source === "string" ? step.arguments.source : null,
    inputs: numberList(step.arguments?.inputs),
    inputsTruncated: numberList(step.arguments?.inputs_truncated),
  };
}

function numberList(value: unknown): number[] {
  return Array.isArray(value) ? value.filter((item): item is number => typeof item === "number") : [];
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return "";
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

/** "4 tool calls · 2 queries · 1.8s" (PRD FR-5.2). */
export function traceSummary(rows: TraceRow[]): string {
  const queries = rows.filter((row) => row.tool === "execute_sql").length;
  const joins = rows.filter((row) => row.tool === STAGED_TOOL).length;
  const total = rows.reduce((sum, row) => sum + (row.durationMs ?? 0), 0);
  const parts = [`${rows.length} tool call${rows.length === 1 ? "" : "s"}`];
  if (queries) parts.push(`${queries} quer${queries === 1 ? "y" : "ies"}`);
  if (joins) parts.push(`${joins} cross-source join${joins === 1 ? "" : "s"}`);
  if (total) parts.push(formatDuration(total));
  return parts.join(" · ");
}

/**
 * A multi-source answer's sources, for its trace and its chips (MS-4).
 *
 * Built from the *run's* list (`connection_ids` on the message), never the
 * thread's: a source added to the conversation afterwards must not look as if
 * this answer used it. Empty for one source, so a single-source trace renders
 * exactly as it did.
 */
export function traceSources(ids: string[], names: Record<string, string>): TraceSources {
  if (ids.length < 2) return { names: {}, ids: {} };
  const byId: Record<string, string> = {};
  const byName: Record<string, string> = {};
  for (const id of ids) {
    const name = names[id] ?? "Removed source";
    byId[id] = name;
    byName[name] = id;
  }
  return { names: byId, ids: byName };
}
